/**
 * Pure month-period helpers for the Bulk Invoices page (Trello #363).
 *
 * Deliberately free of React / MUI / api imports so the Playwright walkthrough
 * spec can import the SAME label formatter the dropdown renders, instead of
 * re-implementing it (an `ai_review` finding: a forked `monthLabel` in the spec
 * had already drifted from the page's out-of-range guard, which would make a
 * selector silently miss). Importing the view itself into a node-side spec
 * pulls in the whole app module graph, so the shared piece lives here.
 */

import type { Organization } from '../services/api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** `2026-06` → `June 2026`. Returns the input unchanged if it isn't a valid period. */
export function formatPeriodLabel(period: string): string {
  const [year, month] = period.split('-');
  const name = MONTH_NAMES[Number(month) - 1];
  return name && year ? `${name} ${year}` : period;
}

/** The last `count` months as `YYYY-MM`, newest first. */
export function recentPeriods(count: number, now: Date = new Date()): string[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
}

/**
 * Months an admin may pick: next month first (subscriptions bill in advance,
 * per Trello #360), then the last two years — so a retroactive range is
 * reachable without a free-text date field.
 */
export function selectablePeriods(now: Date = new Date()): string[] {
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextPeriod = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
  return [nextPeriod, ...recentPeriods(24, now)];
}

/** Whole months from `from` to `to` inclusive; 0 when the range is reversed. */
export function monthSpan(from: string, to: string): number {
  if (from > to) return 0;
  const [fy, fm] = from.split('-').map(Number) as [number, number];
  const [ty, tm] = to.split('-').map(Number) as [number, number];
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** Whether an org has the billing fields an invoice needs. */
export function hasBillingDetails(org: Organization): boolean {
  return Boolean(org.billingName?.trim() && org.billingAddress?.trim());
}
